import {
  Circle,
  Document,
  Font,
  G,
  Page,
  Path,
  Rect,
  StyleSheet,
  Svg,
  Text as PdfText,
  Image,
  View,
} from '@react-pdf/renderer';
import * as d3 from 'd3';
import React from 'react';

// ─── Font registration ──────────────────────────────────────────────────────────
// Match the app's typography (Lora for titles, Urbanist for body) using the TTFs we
// vendored under /public/fonts. Registered once at module load.
Font.register({
  family: 'Urbanist',
  fonts: [
    { src: '/fonts/Urbanist-Regular.ttf', fontWeight: 400 },
    { src: '/fonts/Urbanist-Medium.ttf', fontWeight: 500 },
    { src: '/fonts/Urbanist-SemiBold.ttf', fontWeight: 600 },
    { src: '/fonts/Urbanist-Bold.ttf', fontWeight: 700 },
    { src: '/fonts/Urbanist-ExtraBold.ttf', fontWeight: 800 },
    { src: '/fonts/Urbanist-Black.ttf', fontWeight: 900 },
  ],
});
Font.register({
  family: 'Lora',
  fonts: [
    // Upright Lora — registered as `normal` so plain `fontFamily: 'Lora'` resolves.
    { src: '/fonts/Lora-Regular.ttf', fontWeight: 400 },
    { src: '/fonts/Lora-Medium.ttf', fontWeight: 500 },
    { src: '/fonts/Lora-Bold.ttf', fontWeight: 700 },
    // Italic Lora — the live UI's `loraSectionTitle` is Lora italic at weight 500,
    // so callers can opt-in with `fontStyle: 'italic'` and pick the right weight here.
    { src: '/fonts/Lora-Italic.ttf', fontWeight: 400, fontStyle: 'italic' },
    { src: '/fonts/Lora-MediumItalic.ttf', fontWeight: 500, fontStyle: 'italic' },
    { src: '/fonts/Lora-BoldItalic.ttf', fontWeight: 700, fontStyle: 'italic' },
  ],
});

import {
  TCortexOrganizationInsightsV1,
  TCortexOrganizationInsightsV2,
  TCortexRecommendationV1,
  TCortexRecommendationV2,
} from '@/types/Cortex.types';
import { THealthDomain } from '@/types/Health.types';
import {
  TPeerSupportDashboard,
  TPeerSupportEventOutcome,
  TPeerSupportHistoricalDataItem,
} from '@/types/PeerSupport.types';
import { TOrganizationReportDomainBreakdownDto } from '@/types/Organization.types';
import { getColorFromPercentage } from '@/utils/getColorFromPercentage';
import svgFiles from '@/assets/svg/svgFiles';

// ─── Theme tokens ───────────────────────────────────────────────────────────────
// Hex values mirror the app's palette so the PDF reads as the same product.
const C = {
  pageBg: '#F0F8FF',
  cardBg: '#FFFFFF',
  cardBorder: '#E5EBF1',
  darkBlue: '#002548',
  mediumBlue: '#004179',
  mediumGray: '#7C9EBC',
  red: '#D84545',
  orange: '#FF8811',
  primary: '#4E1AEF',
  yellow: '#FCD446',
  teal: '#00D2AD',
  skyBlue: '#1198FF',
  purplePink: '#D54FED',
  turquoise: '#4DD1E8',
  separator: '#E5EBF1',
};

// 7 domains in dashboard order.
const DOMAIN_ORDER: THealthDomain[] = ['physical', 'mental', 'social', 'vocational', 'spiritual', 'purpose', 'fun'];
const DOMAIN_CHART_COLORS: Record<THealthDomain, string> = {
  physical: C.primary,
  mental: C.orange,
  social: C.yellow,
  vocational: C.teal,
  spiritual: C.skyBlue,
  purpose: C.purplePink,
  fun: C.turquoise,
};
const DOMAIN_LABELS: Record<THealthDomain, string> = {
  physical: 'Physical',
  mental: 'Mental',
  social: 'Social',
  vocational: 'Vocational',
  spiritual: 'Spiritual',
  purpose: 'Purpose',
  fun: 'Fun',
};
const OUTCOME_COLORS = ['#002548', '#33516D', '#667C91', '#99A8B6', '#CCD3DA'];

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  page: {
    padding: 24,
    backgroundColor: C.pageBg,
    fontFamily: 'Urbanist',
    fontSize: 9,
    color: C.darkBlue,
    gap: 10,
  },
  header: { marginBottom: 12 },
  // Mirror the live UI's `loraSectionTitle` variant — Lora italic at weight 500.
  title: { fontSize: 22, fontFamily: 'Lora', fontWeight: 500, fontStyle: 'italic', color: C.mediumBlue },
  subtitle: { fontSize: 10, color: C.mediumGray, marginTop: 3 },
  row: { flexDirection: 'row', gap: 10, marginBottom: 10 },
  card: {
    backgroundColor: C.cardBg,
    borderRadius: 8,
    padding: 12,
    border: `0.5pt solid ${C.cardBorder}`,
    flexGrow: 1,
    flexBasis: 0,
  },
  // Card titles in the live UI are weight 500 — keeping the bold-bold (700) here read as far too heavy.
  cardTitle: { fontSize: 12, fontFamily: 'Urbanist', fontWeight: 500, color: C.darkBlue, marginBottom: 6 },
  // Tiny caps section labels match `SectionLabel` on screen — weight 900 at 12px.
  sectionLabel: {
    fontSize: 7,
    fontFamily: 'Urbanist',
    fontWeight: 900,
    color: C.mediumGray,
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  statRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 4 },
  // Stat label (urbanistSemiBoldRegular w/ inline 500) / value (urbanistBold w/ inline 800).
  statLabel: { fontSize: 10, fontFamily: 'Urbanist', fontWeight: 500, color: C.mediumGray },
  statValue: { fontSize: 14, fontFamily: 'Urbanist', fontWeight: 800, color: C.darkBlue },
  // Body / muted text are 400 / 500 — readable on screen, much easier on the eye than 700.
  descriptionTextMedium: {
    fontSize: 10,
    fontFamily: 'Urbanist',
    fontWeight: 500,
    color: C.mediumBlue,
    lineHeight: 1.4,
  },
  descriptionTextBold: { fontSize: 10, fontFamily: 'Urbanist', fontWeight: 500, color: C.mediumBlue, lineHeight: 1.4 },
  bodyText: { fontSize: 9, fontFamily: 'Urbanist', fontWeight: 400, color: C.darkBlue, lineHeight: 1.4 },
  mutedText: { fontSize: 8, fontFamily: 'Urbanist', fontWeight: 500, color: C.mediumGray, lineHeight: 1.4 },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 6,
  },
});

// ─── Props ──────────────────────────────────────────────────────────────────────

export interface WholenessReportPdfProps {
  organizationName: string;
  periodLabel: string; // e.g. "Feb 2026"
  selectedMonth: string; // 'yyyy-m'

  previousPeriodLabel: string;
  currentPeriodLabel: string;

  changeSincePrevMonthLabel: string;
  isChangeDirectionDown: boolean;

  // Wholeness Observation block.
  previousPeriodWholenessScore: number;
  currentPeriodWholenessScore: number;
  engagementNarrative?: string;
  domainsOfStrengthPrevious: TOrganizationReportDomainBreakdownDto[];
  domainsOfStrengthCurrent: TOrganizationReportDomainBreakdownDto[];
  domainsOfOpportunityPrevious: TOrganizationReportDomainBreakdownDto[];
  domainsOfOpportunityCurrent: TOrganizationReportDomainBreakdownDto[];

  // Dashboard counts shown in the stats card.
  stats: {
    employeesInRoster: number;
    screenersSubmitted: number;
    totalSubmitters: number;
    peerSupportMembers: number;
    memberCareSessions: number;
  };

  // Peer Support Engagement.
  peerSupportEngagement?: TPeerSupportDashboard;

  // Cortex AI recommendations — one of the two will be populated depending on version.
  cortexInsightsV1?: TCortexOrganizationInsightsV1 | null;
  cortexInsightsV2?: TCortexOrganizationInsightsV2 | null;

  notes?: string;
  copyrightYear: number;
}

// ─── Document ───────────────────────────────────────────────────────────────────

export const WholenessReportPdfDocument: React.FC<WholenessReportPdfProps> = (props) => (
  <Document title={`${props.organizationName} - Wholeness Report - ${props.periodLabel}`}>
    <Page size="A4" orientation="portrait" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <PdfText style={styles.title}>MyOmnia Wholeness Report</PdfText>
        <PdfText style={styles.subtitle}>
          Here&apos;s your wholeness report for {props.organizationName} — {props.periodLabel}.
        </PdfText>
      </View>

      {/* Score + Employees Row */}
      <View style={styles.row}>
        <View style={styles.card}>
          <PdfText style={[styles.cardTitle, { textAlign: 'center' }]}>Organization&apos;s Wholeness Score</PdfText>
          {/* HStack */}
          <View
            style={[
              styles.row,
              { gap: '30px', marginTop: '20px', marginBottom: '10px', alignItems: 'center', justifyContent: 'center' },
            ]}
          >
            <View style={{ gap: '10px', alignItems: 'center' }}>
              <ScoreCircle size={40} stroke={4} fontSize={12} value={props.previousPeriodWholenessScore} />
              <PdfText style={[styles.mutedText, { marginTop: 4, fontFamily: 'Urbanist', fontWeight: 500 }]}>
                {props.previousPeriodLabel}
              </PdfText>
            </View>

            <Image style={{ width: '60px', height: '28px', marginBottom: '14px' }} source={svgFiles.arrowRightLong} />

            <View style={{ gap: '10px', alignItems: 'center' }}>
              <ScoreCircle size={40} stroke={4} fontSize={12} value={props.currentPeriodWholenessScore} />
              <PdfText style={[styles.mutedText, { marginTop: 4, fontFamily: 'Urbanist', fontWeight: 500 }]}>
                {props.currentPeriodLabel}
              </PdfText>
            </View>
          </View>

          <View style={[styles.row, { width: '100%', justifyContent: 'center', marginTop: '10px', gap: 3 }]}>
            <PdfText style={styles.descriptionTextMedium}>Change Since Previous Month -</PdfText>
            <PdfText style={[styles.descriptionTextBold, { color: props.isChangeDirectionDown ? C.red : C.teal }]}>
              {props.changeSincePrevMonthLabel}
            </PdfText>
          </View>
        </View>

        <View style={styles.card}>
          <StatRow label="Employees on Roster" value={props.stats.employeesInRoster} />
          <StatRow label="# of Screeners Submitted" value={props.stats.screenersSubmitted} />
          <StatRow label="Peer Support Members" value={props.stats.peerSupportMembers} />
          <StatRow label="Member Care Contacts" value={props.stats.totalSubmitters} />
          <StatRow label="Sessions Utilized" value={props.stats.memberCareSessions} />
        </View>
      </View>

      {/* Strengths + Opportunities Row */}
      <View style={styles.row}>
        <View style={styles.card}>
          <PdfText style={styles.cardTitle}>Team Strengths</PdfText>
          <View
            style={[
              styles.row,
              {
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '14px',
                width: '100%',
                marginTop: '10px',
              },
            ]}
          >
            <View style={{ alignItems: 'flex-start' }}>
              <PdfText style={styles.sectionLabel}>PREVIOUS MONTH</PdfText>
              <View style={{ alignItems: 'flex-start', gap: '12px' }}>
                {props.domainsOfStrengthPrevious.length > 0 ? (
                  props.domainsOfStrengthPrevious.map((d) => (
                    <DomainScoreRow
                      key={d.domainKey}
                      name={d.domainName}
                      score={Math.round(d.score)}
                      subdomains={d.subdomains}
                    />
                  ))
                ) : (
                  <PdfText style={styles.mutedText}>Not enough screener data yet.</PdfText>
                )}
              </View>
            </View>
            <View style={{ alignItems: 'flex-start' }}>
              <PdfText style={styles.sectionLabel}>SELECTED MONTH</PdfText>
              <View style={{ alignItems: 'flex-start', gap: '12px' }}>
                {props.domainsOfStrengthCurrent.length > 0 ? (
                  props.domainsOfStrengthCurrent.map((d) => (
                    <DomainScoreRow
                      key={d.domainKey}
                      name={d.domainName}
                      score={Math.round(d.score)}
                      subdomains={d.subdomains}
                    />
                  ))
                ) : (
                  <PdfText style={styles.mutedText}>Not enough screener data yet.</PdfText>
                )}
              </View>
            </View>
          </View>
        </View>

        <View style={styles.card}>
          <PdfText style={styles.cardTitle}>Opportunities for Growth</PdfText>
          <View
            style={[
              styles.row,
              {
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '14px',
                width: '100%',
                marginTop: '10px',
              },
            ]}
          >
            <View style={{ alignItems: 'flex-start' }}>
              <PdfText style={styles.sectionLabel}>PREVIOUS MONTH</PdfText>
              <View style={{ alignItems: 'flex-start', gap: '12px' }}>
                {props.domainsOfOpportunityPrevious.length > 0 ? (
                  props.domainsOfOpportunityPrevious.map((d) => (
                    <DomainScoreRow
                      key={d.domainKey}
                      name={d.domainName}
                      score={Math.round(d.score)}
                      subdomains={d.subdomains}
                    />
                  ))
                ) : (
                  <PdfText style={styles.mutedText}>Not enough screener data yet.</PdfText>
                )}
              </View>
            </View>
            <View style={{ alignItems: 'flex-start' }}>
              <PdfText style={styles.sectionLabel}>SELECTED MONTH</PdfText>
              <View style={{ alignItems: 'flex-start', gap: '12px' }}>
                {props.domainsOfOpportunityCurrent.length > 0 ? (
                  props.domainsOfOpportunityCurrent.map((d) => (
                    <DomainScoreRow
                      key={d.domainKey}
                      name={d.domainName}
                      score={Math.round(d.score)}
                      subdomains={d.subdomains}
                    />
                  ))
                ) : (
                  <PdfText style={styles.mutedText}>Not enough screener data yet.</PdfText>
                )}
              </View>
            </View>
          </View>
        </View>
      </View>

      {/* Peer Support Engagement */}
      <View style={[styles.card, { marginBottom: 10, minHeight: '150px' }]}>
        <PdfText style={styles.cardTitle}>Peer Support Engagement Data — {props.periodLabel}</PdfText>
        <PeerSupportSection
          engagement={props.peerSupportEngagement}
          selectedMonth={props.selectedMonth}
          periodLabel={props.periodLabel}
        />
      </View>
    </Page>

    <Page size="A4" orientation="portrait" style={styles.page}>
      {/* MyOmnia Recommends — full width */}
      <View style={[styles.card, { width: '100%', marginBottom: 10 }]}>
        <PdfText style={styles.cardTitle}>MyOmnia Recommends</PdfText>
        <PdfText style={styles.sectionLabel}>SKILLS TO FOCUS ON</PdfText>
        <RecommendationsList v1={props.cortexInsightsV1} v2={props.cortexInsightsV2} />
      </View>

      {/* Notes — full width, below recommendations */}
      <View style={[styles.card, { width: '100%', marginBottom: 10 }]}>
        <PdfText style={styles.cardTitle}>Notes for {props.periodLabel}</PdfText>
        {props.notes && props.notes.trim().length > 0 ? (
          <PdfText style={styles.bodyText}>{props.notes}</PdfText>
        ) : (
          <PdfText style={styles.mutedText}>—</PdfText>
        )}
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <PdfText style={[styles.mutedText, { fontFamily: 'Urbanist', fontWeight: 500, color: C.mediumBlue }]}>
          MyOmnia · Copyright © {props.copyrightYear}
        </PdfText>
      </View>
    </Page>
  </Document>
);

// ─── Subcomponents ──────────────────────────────────────────────────────────────

const StatRow: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <View style={styles.statRow}>
    <PdfText style={styles.statLabel}>{label}</PdfText>
    <PdfText style={styles.statValue}>{value}</PdfText>
  </View>
);

const ScoreCircle: React.FC<{ value: number; size?: number; stroke?: number; fontSize?: number }> = ({
  value,
  size = 72,
  stroke = 7,
  fontSize = 16,
}) => {
  const radius = (size - stroke) / 2;
  const center = size / 2;
  const clamped = Math.max(0, Math.min(100, value));
  // Threshold-based ring colour (red < 40, yellow < 80, teal ≥ 80) — matches the live UI.
  const ringColor = getColorFromPercentage(clamped);

  // pdfkit (under @react-pdf/renderer) rejects any 0 in a dash pattern. Instead of relying on
  // strokeDasharray, draw the filled arc as a `<Path>` and skip dasharray entirely.
  //  - 0%   → no foreground arc (just the gray background ring)
  //  - 100% → foreground is a full circle
  //  - 1-99% → arc path computed via d3.arc with the right end angle
  const renderForeground = (): React.ReactNode => {
    if (clamped <= 0) return null;
    if (clamped >= 100) {
      return <Circle cx={center} cy={center} r={radius} stroke={ringColor} strokeWidth={stroke} fill="none" />;
    }
    const startAngle = 0;
    const endAngle = -(clamped / 100) * 2 * Math.PI;
    const arcPath = d3.arc<{ startAngle: number; endAngle: number }>().innerRadius(radius).outerRadius(radius)({
      startAngle,
      endAngle,
    });
    if (!arcPath) return null;
    return (
      <Path
        d={arcPath}
        stroke={ringColor}
        strokeWidth={stroke}
        strokeLinecap="round"
        fill="none"
        transform={`translate(${center} ${center})`}
      />
    );
  };

  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <Circle cx={center} cy={center} r={radius} stroke={C.separator} strokeWidth={stroke} fill="none" />
      {renderForeground()}
      {/* SVG `<text>` positions by baseline. textAnchor="middle" centres horizontally on `x`;
          we nudge `y` down by ~0.35 × fontSize so the visual centre sits on the circle centre. */}
      <PdfText
        x={center}
        y={center + fontSize * 0.35}
        textAnchor="middle"
        style={{ fontSize, fontFamily: 'Urbanist', fontWeight: 700, fill: C.darkBlue }}
      >
        {Math.round(value)}
      </PdfText>
    </Svg>
  );
};

const DomainScoreRow: React.FC<{
  name: string;
  score: number;
  subdomains: TOrganizationReportDomainBreakdownDto['subdomains'];
}> = ({ name, score, subdomains }) => (
  <View>
    <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 3 }}>
      <ScoreCircle value={score} size={28} stroke={3} fontSize={9} />
      <PdfText style={{ fontSize: 10, marginLeft: 8, color: C.darkBlue }}>{name}</PdfText>
    </View>
    <View style={{ paddingLeft: '10px', alignItems: 'flex-start', gap: 0.5 }}>
      {subdomains?.map((subdomain) => (
        <View style={[styles.row, { gap: 2 }]} key={subdomain.name}>
          <View
            style={{
              backgroundColor: getColorFromPercentage(subdomain.score),
              width: '8px',
              height: '8px',
              borderRadius: '16px',
            }}
          />
          <PdfText style={{ fontSize: 8, marginLeft: 4, color: C.darkBlue, fontWeight: 700 }}>
            {subdomain.score}
          </PdfText>
          <PdfText style={{ fontSize: 8, marginLeft: 4, color: C.darkBlue, fontWeight: 500, maxWidth: '100px' }}>
            {subdomain.name}
          </PdfText>
        </View>
      ))}
    </View>
  </View>
);

// ─── Recommendations ────────────────────────────────────────────────────────────

const RecommendationsList: React.FC<{
  v1?: TCortexOrganizationInsightsV1 | null;
  v2?: TCortexOrganizationInsightsV2 | null;
}> = ({ v1, v2 }) => {
  if (v1 && (v1.recommendations?.length ?? 0) > 0) {
    return (
      <View>
        {orderByPriority(v1.recommendations ?? []).map((rec, i) => (
          <RecommendationCardV1Pdf key={i} rec={rec} />
        ))}
      </View>
    );
  }
  if (v2 && (v2.skillsToFocusOn?.length ?? 0) > 0) {
    return (
      <View>
        {orderByPriority(v2.skillsToFocusOn).map((rec, i) => (
          <RecommendationCardV2Pdf key={i} rec={rec} />
        ))}
      </View>
    );
  }
  return <PdfText style={styles.mutedText}>No recommendations available yet.</PdfText>;
};

const RecommendationCardV1Pdf: React.FC<{ rec: TCortexRecommendationV1 }> = ({ rec }) => {
  const color = priorityColor(rec.priority);
  return (
    <View
      style={{
        marginBottom: 6,
        padding: 8,
        backgroundColor: '#F3F4F8',
        borderRadius: 6,
      }}
    >
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
        <PdfText style={{ fontSize: 8, fontFamily: 'Urbanist', fontWeight: 800, color }}>
          {(rec.priority ?? '').toUpperCase()}
        </PdfText>
        <PdfText style={{ fontSize: 8, fontFamily: 'Urbanist', fontWeight: 800, color: C.mediumGray }}>
          {[rec.category, rec.timeline]
            .filter(Boolean)
            .map((s) => s.toUpperCase())
            .join(' · ')}
        </PdfText>
      </View>
      <PdfText style={[styles.bodyText, { fontFamily: 'Urbanist', fontWeight: 600 }]}>{rec.action}</PdfText>
      {rec.expectedImpact ? (
        <PdfText style={[styles.mutedText, { marginTop: 2 }]}>— {rec.expectedImpact}</PdfText>
      ) : null}
    </View>
  );
};

const RecommendationCardV2Pdf: React.FC<{ rec: TCortexRecommendationV2 }> = ({ rec }) => {
  const color = priorityColor(rec.priority);
  const bg = priorityBackground(rec.priority);
  return (
    <View style={{ marginBottom: 6, padding: 8, backgroundColor: bg, borderRadius: 6 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
        <PdfText style={{ fontSize: 8, fontFamily: 'Urbanist', fontWeight: 800, color }}>
          {formatCategory(rec.category)}
        </PdfText>
        <PdfText style={{ fontSize: 8, fontFamily: 'Urbanist', fontWeight: 800, color }}>
          {(rec.priority ?? '').toUpperCase()}
        </PdfText>
      </View>
      <PdfText style={[styles.bodyText, { fontFamily: 'Urbanist', fontWeight: 600 }]}>{rec.text}</PdfText>
      {rec.expectedOutcome ? (
        <PdfText style={[styles.mutedText, { marginTop: 2 }]}>— {rec.expectedOutcome}</PdfText>
      ) : null}
    </View>
  );
};

const PRIORITY_ORDER: Record<string, number> = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
const orderByPriority = <T extends { priority?: string }>(items: T[]): T[] =>
  [...items].sort(
    (a, b) =>
      (PRIORITY_ORDER[(a.priority ?? '').toUpperCase()] ?? 99) -
      (PRIORITY_ORDER[(b.priority ?? '').toUpperCase()] ?? 99),
  );

const priorityColor = (p?: string): string => {
  switch ((p ?? '').toUpperCase()) {
    case 'CRITICAL':
      return C.red;
    case 'HIGH':
      return C.orange;
    default:
      return C.mediumBlue;
  }
};
const priorityBackground = (p?: string): string => {
  switch ((p ?? '').toUpperCase()) {
    case 'CRITICAL':
      return '#FDF2F2';
    case 'HIGH':
      return '#FEF6EF';
    default:
      return '#F3F4F8';
  }
};

const formatCategory = (c?: string) => (c ?? '').replace(/_AND_/g, ' & ').replace(/_/g, ' ').toUpperCase();

// ─── Peer Support Section ───────────────────────────────────────────────────────

const PeerSupportSection: React.FC<{
  engagement?: TPeerSupportDashboard;
  selectedMonth: string;
  periodLabel: string;
}> = ({ engagement, selectedMonth, periodLabel }) => {
  const subjects = buildSubjectsData(engagement);
  const outcomes = buildOutcomesData(engagement);
  const sixMonthTotal = engagement?.historicalData.reduce((s, m) => s + m.totalInteractions, 0) ?? 0;

  const hasHistory = (engagement?.historicalData?.length ?? 0) > 0;
  const hasSubjects = subjects.length > 0;
  const hasOutcomes = outcomes.length > 0;
  const isEmpty = !hasHistory && !hasSubjects && !hasOutcomes;

  if (isEmpty) {
    return (
      <PdfText style={[styles.mutedText, { textAlign: 'center', paddingVertical: 12 }]}>
        No peer support engagement data for {periodLabel}.
      </PdfText>
    );
  }

  return (
    <View style={{ flexDirection: 'row', gap: 16 }}>
      <View style={{ flex: 1, gap: 4, marginTop: 8 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <PdfText style={{ fontSize: 10, fontFamily: 'Urbanist', fontWeight: 600, color: C.darkBlue }}>
            Contacts
          </PdfText>
          <PdfText style={{ fontSize: 8, color: C.mediumGray }}>6 Month Total: {sixMonthTotal}</PdfText>
        </View>
        <ContactsBarChart data={engagement?.historicalData ?? []} selectedMonth={selectedMonth} />
      </View>
      <View style={{ flex: 1, gap: 4, marginTop: 8 }}>
        <PdfText style={{ fontSize: 10, fontFamily: 'Urbanist', fontWeight: 600, color: C.darkBlue, marginBottom: 4 }}>
          Subjects
        </PdfText>
        {hasSubjects ? (
          <DoughnutChartPdf data={subjects} />
        ) : (
          <PdfText style={[styles.mutedText, { marginTop: '14px' }]}>No subject data.</PdfText>
        )}
      </View>
      <View style={{ flex: 1, gap: 4, marginTop: 8 }}>
        <PdfText style={{ fontSize: 10, fontFamily: 'Urbanist', fontWeight: 600, color: C.darkBlue, marginBottom: 4 }}>
          Outcomes
        </PdfText>
        {hasOutcomes ? (
          <DoughnutChartPdf data={outcomes} />
        ) : (
          <PdfText style={[styles.mutedText, { marginTop: '14px' }]}>No outcome data.</PdfText>
        )}
      </View>
    </View>
  );
};

type ChartDatum = { name: string; value: number; color: string };

const buildSubjectsData = (engagement?: TPeerSupportDashboard): ChartDatum[] => {
  const subjects = (engagement?.aggregates?.subjects ?? {}) as Record<string, { count: number }>;
  return DOMAIN_ORDER.map((key) => {
    const entry = subjects[key];
    if (!entry) return null;
    return {
      name: DOMAIN_LABELS[key],
      value: entry.count,
      color: DOMAIN_CHART_COLORS[key],
    };
  }).filter((d): d is ChartDatum => d !== null);
};

const buildOutcomesData = (engagement?: TPeerSupportDashboard): ChartDatum[] => {
  const outcomes = (engagement?.aggregates?.outcomes ?? {}) as Record<TPeerSupportEventOutcome, number>;
  const entries = Object.entries(outcomes).map(([key, value]) => ({
    name: formatOutcomeLabel(key as TPeerSupportEventOutcome),
    value: value as number,
  }));
  return entries
    .sort((a, b) => b.value - a.value)
    .map((entry, i) => ({
      ...entry,
      color: OUTCOME_COLORS[i] ?? OUTCOME_COLORS[OUTCOME_COLORS.length - 1],
    }));
};

const formatOutcomeLabel = (outcome: TPeerSupportEventOutcome): string =>
  outcome
    .toString()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// ─── Doughnut chart ─────────────────────────────────────────────────────────────

const DoughnutChartPdf: React.FC<{ data: ChartDatum[]; diameter?: number; thickness?: number }> = ({
  data,
  diameter = 86,
  thickness = 16,
}) => {
  const radius = diameter / 2;
  const innerRadius = radius - thickness;
  const total = data.reduce((s, d) => s + d.value, 0);

  const arc = d3.arc<{ startAngle: number; endAngle: number }>().innerRadius(innerRadius).outerRadius(radius);
  const pie = d3
    .pie<ChartDatum>()
    .value((d) => d.value)
    .padAngle(0.02);
  const slices = pie(data);

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 10 }}>
      <Svg width={diameter} height={diameter} viewBox={`0 0 ${diameter} ${diameter}`}>
        <G transform={`translate(${radius} ${radius})`}>
          {slices.map((slice, i) => {
            const d = arc({ startAngle: slice.startAngle, endAngle: slice.endAngle });
            return d ? <Path key={i} d={d} fill={data[i].color} /> : null;
          })}
        </G>
      </Svg>
      <View style={{ flex: 1 }}>
        {data.map((d) => (
          <View key={d.name} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 2 }}>
            <Svg width={8} height={8} viewBox="0 0 8 8" style={{ marginRight: 5 }}>
              <Circle cx={4} cy={4} r={4} fill={d.color} />
            </Svg>
            <PdfText style={{ fontSize: 8, color: C.darkBlue, flex: 1 }} wrap={false}>
              {d.name} <PdfText style={{ fontFamily: 'Urbanist', fontWeight: 600 }}>{d.value}</PdfText>
            </PdfText>
          </View>
        ))}
        <PdfText style={{ fontSize: 7, color: C.mediumGray, marginTop: 2 }}>Total: {total}</PdfText>
      </View>
    </View>
  );
};

// ─── Bar chart (Contacts) ───────────────────────────────────────────────────────

const ContactsBarChart: React.FC<{ data: TPeerSupportHistoricalDataItem[]; selectedMonth: string }> = ({
  data,
  selectedMonth,
}) => {
  if (data.length === 0) {
    return <PdfText style={styles.mutedText}>No contacts in the last 6 months.</PdfText>;
  }

  const chartHeight = 80;
  const labelHeight = 28;
  const barWidth = 8;
  const barGap = 4;
  const columnWidth = barWidth * 2 + barGap + 8;
  const totalWidth = data.length * columnWidth;
  const fullHeight = chartHeight + labelHeight;

  const highest = Math.max(1, ...data.flatMap((m) => [m.peerReported, m.memberRequested]));

  return (
    <Svg width={totalWidth + 10} height={fullHeight + 80} viewBox={`0 0 ${totalWidth} ${fullHeight + 20}`}>
      {data.map((monthly, i) => {
        const x = i * columnWidth + 2;
        const peerHeight = (monthly.peerReported / highest) * chartHeight;
        const memberHeight = (monthly.memberRequested / highest) * chartHeight;
        const isSelected = monthly.month === selectedMonth;
        const monthLabel = monthLabelFromKey(monthly.month);
        return (
          <G key={monthly.month}>
            {isSelected && (
              <Rect
                x={x - 3}
                y={0}
                width={barWidth * 2 + barGap + 6}
                height={chartHeight + 30}
                rx={6}
                ry={6}
                fill="#EDE8FD"
              />
            )}
            <Rect
              x={x}
              y={chartHeight - peerHeight}
              width={barWidth}
              height={peerHeight}
              rx={3}
              ry={3}
              fill={C.primary}
            />
            <Rect
              x={x + barWidth + barGap}
              y={chartHeight - memberHeight}
              width={barWidth}
              height={memberHeight}
              rx={3}
              ry={3}
              fill={C.orange}
            />
            <PdfText
              x={x + 2.5}
              y={chartHeight + 12}
              style={{ fontSize: 8, color: C.mediumGray, textAlign: 'center', marginTop: 4 }}
            >
              {monthLabel}
            </PdfText>
            <PdfText
              x={x + i / 3}
              y={chartHeight + 24}
              style={{ fontSize: 8, color: C.darkBlue, textAlign: 'center', fontFamily: 'Urbanist', fontWeight: 600 }}
            >
              {monthly.peerReported} |{monthly.memberRequested}
            </PdfText>
          </G>
        );
      })}
    </Svg>
  );
};

const monthLabelFromKey = (yyyyM: string): string => {
  const [yearStr, monthStr] = yyyyM.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  if (!year || !month) return yyyyM;
  return new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'short' });
};
